import React, { Suspense, lazy, useEffect, useState, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { trackPageView } from "./utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Handles the OAuth deep link callback when running as native Android app.
// Google redirects to com.bestcars.admin://callback?code=... after login.
const NativeOAuthHandler = () => {
  const navigate = useNavigate();
  const { setToken, setUser } = useAuth();

  useEffect(() => {
    const isNative = window.Capacitor?.isNativePlatform?.() === true;
    if (!isNative) return;

    let listenerHandle = null;

    const setup = async () => {
      const CapApp = window.Capacitor.Plugins.App;
      const Browser = window.Capacitor.Plugins.Browser;

      listenerHandle = await CapApp.addListener('appUrlOpen', async (event) => {
        await Browser.close().catch(() => {});

        const url = new URL(event.url);
        const code = url.searchParams.get('code');
        const error = url.searchParams.get('error');

        if (error || !code) {
          navigate('/admin/login', { state: { error: 'Autenticação cancelada' } });
          return;
        }

        try {
          const resp = await fetch(`${BACKEND_URL}/api/auth/google/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code, redirect_uri: `${window.location.origin}/admin/auth/callback` }),
          });

          if (!resp.ok) {
            const err = await resp.json();
            navigate('/admin/login', { replace: true, state: { error: err.detail } });
            return;
          }

          const data = await resp.json();
          if (data.access_token && setToken) setToken(data.access_token);
          if (data.user && setUser) setUser(data.user);
          navigate('/admin', { replace: true });
        } catch {
          navigate('/admin/login', { replace: true });
        }
      });
    };

    setup();
    return () => { listenerHandle?.remove(); };
  }, [navigate, setToken, setUser]);

  return null;
};
import Navbar from "./components/Layout/Navbar";
import Footer from "./components/Layout/Footer";
import CookieConsent from "./components/CookieConsent";
import SplashScreen from "./components/SplashScreen";
import { PageTransitionProvider, NavigateRegistrar } from "./contexts/PageTransitionContext";
import HomePage from "./pages/HomePage";

const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
const ChatWidget = lazy(() => import("./components/ChatWidget"));
const StockPage = lazy(() => import("./pages/StockPage"));
const CarDetailPage = lazy(() => import("./pages/CarDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const OficinaPage = lazy(() => import("./pages/OficinaPage"));
const ConsultadoriaPage = lazy(() => import("./pages/ConsultadoriaPage"));
const CreditoPage = lazy(() => import("./pages/CreditoPage"));
const CompramosPage = lazy(() => import("./pages/CompramosPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCars = lazy(() => import("./pages/admin/AdminCars"));
const AdminCarForm = lazy(() => import("./pages/admin/AdminCarForm"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminDocuments = lazy(() => import("./pages/admin/AdminDocuments"));
const AdminSiteContent = lazy(() => import("./pages/admin/AdminSiteContent"));
const AuthCallback = lazy(() => import("./pages/admin/AuthCallback"));

const RouteFallback = () => <div className="min-h-screen bg-[#0a0a0a]" />;

const LazyPublicWidgets = () => {
  const [canRender, setCanRender] = useState(false);

  useEffect(() => {
    const schedule = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1200));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const handle = schedule(() => setCanRender(true));
    return () => cancel(handle);
  }, []);

  if (!canRender) return null;

  return (
    <Suspense fallback={null}>
      <WhatsAppButton />
      <ChatWidget />
    </Suspense>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const prev = React.useRef(pathname);
  useEffect(() => {
    // Disable browser's native scroll restoration to prevent conflicts
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    if (pathname !== prev.current) {
      prev.current = pathname;
      // Don't scroll to top on /viaturas — it handles its own scroll restoration
      if (pathname !== '/viaturas') {
        window.scrollTo(0, 0);
      }
      trackPageView(pathname);
    }
  }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return children;
};

const AdminOnlyRoute = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) return <Navigate to="/admin" replace />;
  return children;
};

const PublicLayout = () => (
  <>
    <Navbar />
    <main>
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </main>
    <Footer />
    <CookieConsent />
    <LazyPublicWidgets />
  </>
);

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <NavigateRegistrar />
      <NativeOAuthHandler />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public routes with shared layout */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/viaturas" element={<StockPage />} />
          <Route path="/viaturas/combustivel/:fuelSlug" element={<StockPage />} />
          <Route path="/viaturas/preco/:priceSlug" element={<StockPage />} />
          <Route path="/viaturas/:brandSlug" element={<StockPage />} />
          <Route path="/viatura/:id" element={<CarDetailPage />} />
          <Route path="/sobre" element={<AboutPage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/servicos/oficina" element={<OficinaPage />} />
          <Route path="/servicos/consultadoria" element={<ConsultadoriaPage />} />
          <Route path="/servicos/credito" element={<CreditoPage />} />
          <Route path="/servicos/compramos" element={<CompramosPage />} />
          <Route path="/aviso-legal" element={<LegalPage />} />
          <Route path="/politica-privacidade" element={<LegalPage />} />
          <Route path="/termos-condicoes" element={<LegalPage />} />
          <Route path="/politica-cookies" element={<LegalPage />} />
          <Route path="/resolucao-litigios" element={<LegalPage />} />
        </Route>
        {/* Admin */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/auth/callback" element={<AuthCallback />} />
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="viaturas" element={<AdminCars />} />
          <Route path="viaturas/novo" element={<AdminCarForm />} />
          <Route path="viaturas/:id" element={<AdminCarForm />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="clientes" element={<AdminClients />} />
          <Route path="documentos" element={<AdminDocuments />} />
          <Route path="conteudo" element={<AdminOnlyRoute><AdminSiteContent /></AdminOnlyRoute>} />
          <Route path="utilizadores" element={<AdminOnlyRoute><AdminUsers /></AdminOnlyRoute>} />
        </Route>
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    return !isMobile && !sessionStorage.getItem('bestcars_visited');
  });

  const handleSplashComplete = useCallback(() => {
    sessionStorage.setItem('bestcars_visited', '1');
    setShowSplash(false);
  }, []);

  return (
    <div className="App">
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <BrowserRouter>
        <PageTransitionProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </PageTransitionProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
